/**
 * Opening the Trade Center on a specific tab from a link (2026-10-07).
 *
 * Why: "2 members are interested in your pedal" linked to plain
 * /trade-center, which opens on Received, so the member had to find the
 * Interests tab themselves. Links can now say /trade-center?tab=interests.
 *
 * The instruction is a one-shot NAVIGATION override, not a preference: the
 * Trade Center applies it for the navigation that carries it (even when it
 * is already on screen), then removes it from that history entry, so back /
 * forward and later visits behave exactly as before.
 */
export const TRADE_CENTER_TABS = Object.freeze(['received', 'sent', 'interests']);
export const INTERESTS_TAB_PATH = '/trade-center?tab=interests';

/** The valid tab named by `?tab=` in a location.search string, else null. */
export function parseTradeCenterTab(search) {
  if (typeof search !== 'string' || !search) return null;
  let value = null;
  try {
    value = new URLSearchParams(search).get('tab');
  } catch (e) {
    return null;
  }
  return TRADE_CENTER_TABS.includes(value) ? value : null;
}

/** location.search without the `tab` parameter ('' when nothing is left). */
export function withoutTabParam(search) {
  if (typeof search !== 'string' || !search) return '';
  const params = new URLSearchParams(search);
  params.delete('tab');
  const rest = params.toString();
  return rest ? `?${rest}` : '';
}

/**
 * Is this a MULTI-member inbound interest digest ("N members are interested
 * in your pedal")? Identified by type and digest fields, never by link, so
 * other notifications that happen to link to /trade-center are untouched.
 * Works for both shapes: in-app docs (digest: true, interestCount: number)
 * and FCM push data (all strings: digest: 'true', interestCount: '2').
 * Single-member digests (open the interest) and board-view outcomes (open
 * the listing; they carry `outcome`, not `digest`) are excluded.
 */
export function isMultiMemberInterestDigest({ type, digest, interestCount, outcome } = {}) {
  if (type !== 'listing_interest') return false;
  if (outcome) return false;
  if (digest !== true && digest !== 'true') return false;
  const count = Number(interestCount);
  return Number.isFinite(count) && count > 1;
}

/**
 * Where the in-app bell sends a listing_interest notification. Multi-member
 * digests go to the Interests tab whatever link they stored (older ones
 * stored plain /trade-center); otherwise the stored link wins, exactly as
 * before (single-member digests: /interest/:id; board-view outcomes:
 * /listing/:id), with the old metadata fallbacks.
 */
export function interestNotificationTarget(notification = {}) {
  const m = notification.metadata || {};
  if (isMultiMemberInterestDigest({
    type: notification.type, digest: m.digest, interestCount: m.interestCount, outcome: m.outcome,
  })) {
    return INTERESTS_TAB_PATH;
  }
  // Replies ("interested") stored plain /trade-center until 2026-10-12, which
  // opened on Received; their card is on the Interests tab.
  if (notification.type === 'listing_interest' && m.outcome === 'interested') {
    return INTERESTS_TAB_PATH;
  }
  if (notification.link) return notification.link;
  if (m.interestId) return `/interest/${m.interestId}`;
  if (m.latestInterestId) return `/interest/${m.latestInterestId}`;
  return '/trade-center';
}

/**
 * The tab actually shown. Interests is a beta (isListingInterestEnabled):
 * an account without it, or whose eligibility hasn't loaded yet, sees
 * Received instead, so a link to Interests never shows restricted content
 * and never leaves the page blank. The requested tab is kept, so it appears
 * as soon as eligibility resolves.
 */
export function visibleTradeCenterTab(activeTab, interestEnabled) {
  return activeTab === 'interests' && !interestEnabled ? 'received' : activeTab;
}
